import { useState, useEffect, useRef } from "react";

export default function Programs() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("math");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const programs = {
    math: {
      title: "Toán học Olympic",
      description:
        "Phát triển tư duy logic và khả năng giải quyết vấn đề phức tạp",
      image: "/ToanHoc.jpg",
      features: [
        "Đại số và Hình học nâng cao",
        "Lý thuyết số và Tổ hợp",
        "Giải tích và Xác suất thống kê",
        "Luyện thi IMO và các kỳ thi quốc tế",
      ],
      levels: ["Cơ bản", "Nâng cao", "Olympic quốc gia", "Olympic quốc tế"],
    },
    programming: {
      title: "Lập trình Competitive",
      description:
        "Xây dựng kỹ năng lập trình và thuật toán cho các cuộc thi quốc tế",
      image: "/LapTrinh.jpg",
      features: [
        "Cấu trúc dữ liệu và Thuật toán",
        "Dynamic Programming và Graph Theory",
        "Competitive Programming Techniques",
        "Luyện thi IOI và ICPC",
      ],
      levels: [
        "Python cơ bản",
        "C++ nâng cao",
        "Algorithms",
        "International Contest",
      ],
    },
    ai: {
      title: "Trí tuệ Nhân tạo (AI)",
      description: "Khám phá và ứng dụng AI trong kỷ nguyên công nghệ số",
      image: "/TriTue.jpg",
      features: [
        "Machine Learning và Deep Learning",
        "Computer Vision và NLP",
        "Robotics và IoT Applications",
        "AI Ethics và Future Technology",
      ],
      levels: [
        "AI Foundations",
        "ML Intermediate",
        "Deep Learning",
        "AI Research",
      ],
    },
  };

  const currentProgram = programs[activeTab as keyof typeof programs];

  return (
    <section
      id="programs"
      className="py-20 bg-gradient-to-br from-gray-50 to-blue-50"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Chương trình Đào tạo
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ba lĩnh vực cốt lõi giúp học sinh Việt Nam đủ năng lực cạnh tranh
              và vươn tầm xuất sắc trên đấu trường quốc tế
            </p>
          </div>

          <div className="flex flex-wrap justify-center mb-12 gap-4">
            <button
              onClick={() => setActiveTab("math")}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === "math"
                  ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-blue-50 border border-gray-200"
              }`}
            >
              <i className="ri-calculator-line mr-2"></i>
              Toán học Olympic
            </button>
            <button
              onClick={() => setActiveTab("programming")}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === "programming"
                  ? "bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-green-50 border border-gray-200"
              }`}
            >
              <i className="ri-code-line mr-2"></i>
              Lập trình Competitive
            </button>
            <button
              onClick={() => setActiveTab("ai")}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === "ai"
                  ? "bg-gradient-to-r from-yellow-500 to-red-500 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-yellow-50 border border-gray-200"
              }`}
            >
              <i className="ri-robot-line mr-2"></i>
              Trí tuệ Nhân tạo
            </button>
          </div>

          <div
            ref={sectionRef}
            className={`transform transition-all duration-1000 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-12 opacity-0"
            }`}
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="p-8 lg:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {currentProgram.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {currentProgram.description}
                  </p>

                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">
                      Nội dung chương trình:
                    </h4>
                    <div className="space-y-3">
                      {currentProgram.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mt-1">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">
                      Các cấp độ:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {currentProgram.levels.map((level, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full text-sm font-medium"
                        >
                          {level}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => scrollToSection("contact")}
                    className="bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-600 hover:via-green-600 hover:to-yellow-600 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
                  >
                    Đăng ký học thử
                  </button>
                </div>

                <div className="relative">
                  <img
                    src={currentProgram.image}
                    alt={currentProgram.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Chất lượng vượt trội
              </h4>
              <p className="text-gray-600">
                Chương trình đào tạo chuyên sâu, chất lượng vượt trội cho giáo
                viên và học sinh
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Tầm nhìn quốc tế
              </h4>
              <p className="text-gray-600">
                Kiến tạo tài năng toàn cầu với khả năng cạnh tranh trên đấu
                trường quốc tế
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Phát triển toàn diện
              </h4>
              <p className="text-gray-600">
                Môi trường giáo dục tiên tiến giúp học sinh phát huy tối đa tiềm
                năng
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
